/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import { StatusCodes } from 'http-status-codes';

import {
  listActionsIfRequired,
  unloadActionData
} from '@ibm-watson/shared/modules/actions/store/actions/actions';
import { fetchEntitiesIfRequired } from '@ibm-watson/shared/modules/actions/store/entities/actions';
import { listVariablesIfRequired } from '@ibm-watson/shared/modules/actions/store/variables/actions';
import { fetchNodesIfRequired } from '@ibm-watson/shared/modules/dialog/store/dialog/actions';
import * as releasesAPI from '@ibm-watson/shared/modules/ia/api/releases';
import { updateTaskCompletionStatus } from '@ibm-watson/shared/modules/ia/hooks/useTaskListHelpers';
import { updateAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/actions';
import {
  selectAssistant,
  selectActionSkillId,
  selectDialogSkillId
} from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import {
  listEnvironments,
  updateEnvironment
} from '@ibm-watson/shared/modules/ia/store/environments/actions';
import {
  selectDraftEnvironment,
  selectEnvironmentById,
  selectIsDialogEnabledInDraftEnvironment
} from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import {
  createRelease,
  deleteRelease,
  deployRelease,
  listReleases,
  listReleasesIfRequired
} from '@ibm-watson/shared/modules/ia/store/releases/actions';
import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { listSkills } from '@ibm-watson/shared/modules/ia/store/skills/actions';
import { sleep } from '@ibm-watson/shared/modules/ia/store/skills/helper';
import {
  exportSnapshotInner,
  listSnapshots,
  listSnapshotsIfRequired,
  listSnapshotsWithArgs,
  revertSnapshot
} from '@ibm-watson/shared/modules/ia/store/snapshots/actions';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import { extractErrorMessage } from '@ibm-watson/shared/modules/ia/util/rest';
import {
  createdObjectEvents,
  ranProcessEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { Environment } from '../../../../ts/assistant';
import { TaskListKey } from '../../../../ts/preferences';
import { PublishStatus, Release } from '../../../../ts/release';
import { ReactivateSession } from '../../../../ts/session';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import { listCollections } from '../../../actions/store/collections/actions';
import { fetchIntentsIfRequired } from '../../../actions/store/intents/actions';
import { selectIsAccessUpdatesEnabled } from '../features/selectors';
import { selectIsNewIa } from '../instance/selectors';
import { selectReleaseById } from '../releases/selectors';
import { selectIsPublishing, selectShouldSyncChanges } from './selectors';

export type RevertVersion = {
  assistant_id: string;
  targetVersion: Release;
  description: string;
  saveDraftContent: boolean;
};

export type PublishVersion = {
  assistant_id: string;
  environment_ids: string[];
  description: string;
};

export type GetPublishStatus = {
  assistant_id: string;
};

type ToggleDraftDialogIfNeeded = {
  targetVersion: Release;
};

/**
 * Overwrite the draft content with the content from `targetVersion`
 */
export const revertVersion = createAsyncThunk<
  void,
  RevertVersion,
  CreateAsyncThunk
>('publish/revertVersion', async (params, { dispatch, getState }) => {
  const { assistant_id, targetVersion, description, saveDraftContent } = params;
  try {
    const actionSkillId = selectActionSkillId(getState());
    const dialogSkillId = selectDialogSkillId(getState());

    // Revert action skill
    const actionsSnapshotId = targetVersion?.content?.skills.find(
      s => s.skill_id === actionSkillId
    )?.snapshot;

    if (saveDraftContent) {
      await dispatch(
        createRelease({
          assistant_id,
          description,
          waitUntilCompleted: true
        })
      );
    }
    await dispatch(
      revertSnapshot({
        skillId: actionSkillId,
        snapshotId: actionsSnapshotId
      })
    );

    // Revert dialog skill
    const dialogSnapshotId = targetVersion?.content?.skills.find(
      s => s.skill_id === dialogSkillId
    )?.snapshot;

    if (dialogSkillId && dialogSnapshotId) {
      await dispatch(
        revertSnapshot({
          skillId: dialogSkillId,
          snapshotId: dialogSnapshotId
        })
      );
    }

    await dispatch(toggleDraftDialogIfNeeded({ targetVersion }));

    dispatch(
      addMessage({
        message: `Your draft content is now replaced with V${targetVersion.release}`,
        status: ToastStatus.SUCCESS
      })
    );

    ranProcessEvents.revert(
      {
        resultValue: 'success',
        successFlag: true
      },
      {
        waTargetEnvironmentId: assistant_id,
        waTargetVersion: targetVersion.release
      }
    );

    const promises = [
      dispatch(listEnvironments()),
      dispatch(listSkills(assistant_id)),
      dispatch(listSnapshots(actionSkillId))
    ];

    if (dialogSkillId) {
      promises.push(dispatch(listSnapshots(dialogSkillId)));
    }

    await Promise.all<unknown>(promises);

    dispatch(unloadActionData());
  } catch (error) {
    ranProcessEvents.revert(
      {
        resultValue: getErrorString(error),
        successFlag: false
      },
      {
        waTargetEnvironmentId: assistant_id,
        waTargetVersion: targetVersion.release
      }
    );

    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const exportVersion = createAsyncThunk<void, Release, CreateAsyncThunk>(
  'publish/exportVersion',
  async (release, { dispatch, getState }) => {
    try {
      const assistant = selectAssistant(getState());
      const actionSkillId = selectActionSkillId(getState());
      const dialogSkillId = selectDialogSkillId(getState());
      const skills = release?.content?.skills ?? [];

      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());

      // Download the dialog snapshot
      const dialogSnapshotId = skills.find(
        s => s.skill_id === dialogSkillId
      )?.snapshot;

      if (dialogSnapshotId) {
        await exportSnapshotInner(
          dialogSkillId,
          useNewIAUpdatedAccess,
          assistantId,
          dialogSnapshotId,
          `${assistant.name}-dialog`,
          release.release
        );
      }

      // Download the action snapshot
      const actionSnapshotId = skills.find(
        s => s.skill_id === actionSkillId
      )?.snapshot;

      if (actionSnapshotId) {
        await exportSnapshotInner(
          actionSkillId,
          useNewIAUpdatedAccess,
          assistantId,
          actionSnapshotId,
          `${assistant.name}-action`,
          release.release
        );
      }
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const deleteVersion = createAsyncThunk<void, Release, CreateAsyncThunk>(
  'publish/deleteVersion',
  async (version, { dispatch, getState }) => {
    try {
      const skillId = selectActionSkillId(getState());
      const assistant_id = getAssistantId(getState());
      const release = version.release;

      await dispatch(deleteRelease({ assistant_id, release }));

      await dispatch(listSnapshots(skillId));
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

type SwitchVersion = {
  release: string;
  environment_id: string;
};

/**
 * Switch the version of any environment with an existing release
 */
export const switchVersion = createAsyncThunk<
  Environment,
  SwitchVersion,
  CreateAsyncThunk
>('publish/switchVersion', async (params, { dispatch, getState }) => {
  const assistant_id = getAssistantId(getState());
  const environment = selectEnvironmentById(getState(), params.environment_id);
  const release = params.release;

  try {
    const actionSkillId = selectActionSkillId(getState());
    const dialogSkillId = selectDialogSkillId(getState());
    await dispatch(
      deployRelease({
        assistant_id,
        release,
        environment
      })
    ).unwrap();

    const promises = [
      dispatch(listReleases()),
      dispatch(listSkills(assistant_id)),
      dispatch(listSnapshots(actionSkillId))
    ];

    if (dialogSkillId) {
      promises.push(dispatch(listSnapshots(dialogSkillId)));
    }

    await Promise.all(promises as never);

    ranProcessEvents.switchVersion(
      {
        resultValue: 'success',
        successFlag: true
      },
      {
        waEnvironmentId: assistant_id,
        waTargetEnvironmentId: environment.environment_id,
        waTargetVersion: release
      }
    );
    await dispatch(
      addMessage({
        message: `V${release} was assigned to the ${environment.name} environment`,
        status: ToastStatus.SUCCESS
      })
    );

    return selectEnvironmentById(getState(), params.environment_id);
  } catch (error) {
    ranProcessEvents.switchVersion(
      {
        resultValue: getErrorString(error),
        successFlag: false
      },
      {
        waEnvironmentId: assistant_id,
        waTargetEnvironmentId: environment.environment_id,
        waTargetVersion: release
      }
    );

    dispatch(addErrorMessage(error));
    throw error;
  }
});

/**
 * Checks if there are changes in the environment that should be published
 * @params {string} params.sourceAssistantId Parent assistant
 */
export const syncChanges = createAsyncThunk<void, void, CreateAsyncThunk>(
  'publish/syncChanges',
  async (_, { dispatch, getState }) => {
    try {
      const actionSkillId = selectActionSkillId(getState());
      const dialogSkillEnabled = selectIsDialogEnabledInDraftEnvironment(
        getState()
      );
      const dialogSkillId = selectDialogSkillId(getState());

      const promises = [];

      promises.push(
        dispatch(
          listReleasesIfRequired({ reactivateSession: ReactivateSession.NEVER })
        )
      );
      promises.push(
        dispatch(listActionsIfRequired(actionSkillId, ReactivateSession.NEVER))
      );
      promises.push(
        dispatch(
          listVariablesIfRequired(actionSkillId, ReactivateSession.NEVER)
        )
      );
      promises.push(
        dispatch(
          listSnapshotsIfRequired({
            skillId: actionSkillId,
            reactivateSession: ReactivateSession.NEVER
          })
        )
      );

      if (dialogSkillEnabled && dialogSkillId) {
        promises.push(dispatch(fetchNodesIfRequired(dialogSkillId)));
        promises.push(
          dispatch(
            fetchIntentsIfRequired({
              skillId: dialogSkillId,
              options: { page_limit: 100000 }
            })
          )
        );
        promises.push(
          dispatch(
            fetchEntitiesIfRequired({
              skillId: dialogSkillId,
              options: { page_limit: 100000 },
              includeSystemEntities: true
            })
          )
        );
        promises.push(
          dispatch(
            listSnapshotsIfRequired({
              skillId: dialogSkillId,
              reactivateSession: ReactivateSession.NEVER
            })
          )
        );
      }
      promises.push(dispatch(listCollections()));

      await Promise.all(promises);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => selectShouldSyncChanges(getState())
  }
);

/**
 * Creates a release and publish changes to the live environment
 *
 * @params {string} params.assistant_id Parent assistant
 * @params {string} params.description the description for the new release
 */
export const publishVersion = createAsyncThunk<
  Pick<Release, 'release' | 'status'>,
  PublishVersion,
  CreateAsyncThunk
>('publish/publishVersion', async (params, { dispatch, getState }) => {
  const { assistant_id, environment_ids, description } = params;

  try {
    if (environment_ids.length === 0) {
      throw new Error('No environments selected');
    }

    const release = await releasesAPI.publishRelease(
      assistant_id,
      description,
      // Publish to live if multi environments is disabled
      environment_ids
    );

    // Inject the created release information into Redux so the interface
    // immediately shows the release being created.
    dispatch(createRelease.fulfilled(release as Release, null, null));

    // Wait until the publish process has finished end-to-end (i.e. the
    // release has been created and deployed).
    await dispatch(checkPublishProgress(assistant_id));

    createdObjectEvents.release({
      object: `${assistant_id}--#${release.release}`,
      resultValue: 'success',
      successFlag: true
    });

    await dispatch(
      addMessage({
        message: `You have successfully updated your environment version`,
        status: ToastStatus.SUCCESS
      })
    );

    dispatch(
      updateTaskCompletionStatus(TaskListKey.HAS_PUBLISHED_ASSISTANT, true)
    );

    ranProcessEvents.publish(
      {
        resultValue: 'success',
        successFlag: true
      },
      {
        waEnvironmentId: assistant_id,
        waTargetEnvironmentId: environment_ids[0],
        waTargetEnvironmentIds: environment_ids,
        waTargetVersion: release?.release
      }
    );

    // Update the releases and return the new release
    await dispatch(listReleases());
    return selectReleaseById(getState(), release.release);
  } catch (error) {
    console.error(error, getErrorString(error));

    ranProcessEvents.publish(
      {
        resultValue: getErrorString(error),
        successFlag: false
      },
      {
        waEnvironmentId: assistant_id,
        waTargetEnvironmentId: environment_ids[0],
        waTargetEnvironmentIds: environment_ids
      }
    );

    // Since we print the error message from Store as-is, let us replace
    // any mentions of snapshots with versions.
    if (
      error.response?.data?.extra?.errorMessage?.includes(
        'snapshots limit exceeded'
      )
    ) {
      error.response.data.error =
        'Maximum versions exceeded: delete a version and try again.';
    }

    createdObjectEvents.release({
      object: `${assistant_id}--`,
      resultValue: getErrorString(error),
      successFlag: false
    });

    // If the publish operation failed because another publish operation is
    // ongoing between the same assistant and environment, refresh the
    // list of releases (so the UI reflects this) and indicate to the user
    // why the operation could not be started.
    if (error.response?.status === StatusCodes.CONFLICT) {
      dispatch(listReleases());
      dispatch(
        addMessage({
          message: extractErrorMessage(error),
          values: error.values,
          status: ToastStatus.INFO,
          transactionId: error.transactionId
        })
      );
    } else {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
});

/**
 * Store doesn't update the dialog `disabled` flag during a revert so we need
 * to check if draft needs to be updated or not based on dialog state in `targetVersion`.
 */
export const toggleDraftDialogIfNeeded = createAsyncThunk<
  void,
  ToggleDraftDialogIfNeeded,
  CreateAsyncThunk
>(
  'publish/toggleDraftDialogIfNeeded',
  async ({ targetVersion }, { dispatch, getState }) => {
    try {
      const isDialogEnabledInDraft = selectIsDialogEnabledInDraftEnvironment(
        getState()
      );
      let disabled = false;

      // version has dialog but draft doesn't, enable dialog
      if (targetVersion?.has_dialog && !isDialogEnabledInDraft) {
        disabled = false;
      } else if (!targetVersion?.has_dialog && isDialogEnabledInDraft) {
        // version doesn't have dialog but draft does, disable dialog
        disabled = true;
      } else {
        // Do not change the dialog disabled state
        return;
      }

      const skill_id = selectDialogSkillId(getState());
      const environment = selectDraftEnvironment(getState());
      const assistant = selectAssistant(getState());

      const skill_references = environment?.skill_references.map(sr => ({
        ...sr,
        disabled: sr.skill_id === skill_id ? disabled : sr.disabled
      }));

      if (skill_id) {
        if (assistant?.skill_references) {
          // FIXME: remove this condition once store is deployed in prod https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
          await dispatch(
            updateAssistant({
              assistant_id: environment.environment_id,
              skill_references
            })
          );
        } else {
          await dispatch(
            updateEnvironment({
              assistant_id: assistant.assistant_id,
              environment_id: environment.environment_id,
              skill_references
            })
          );
        }
      }
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const checkPublishProgress =
  (assistant_id: string): AsyncAppThunk<Promise<void>> =>
  async dispatch => {
    let waitTimeInMs = 10000; // 10 seconds

    // Wait until the release is ready and dispatch updates in the meantime
    let is_publishing = true;

    do {
      try {
        await sleep(waitTimeInMs);
        const result = await dispatch(
          getPublishStatus({
            assistant_id
          })
        ).unwrap();

        is_publishing = result.is_publishing;
      } catch (error) {
        // Wait a bit more if there is an error
        waitTimeInMs = 120000;
      }
    } while (is_publishing);
  };

/**
 * Check the status of the publish operation. When the operation is done,
 * request information to update the release, snapshot and environment data.
 */
export const getPublishStatus = createAsyncThunk<
  PublishStatus,
  GetPublishStatus,
  CreateAsyncThunk
>(
  'publish/getPublishStatus',
  async ({ assistant_id }, { dispatch, getState }) => {
    const isPublishing = selectIsPublishing(getState());

    try {
      const results = await releasesAPI.getPublishStatus(assistant_id);

      if (isPublishing && !results.is_publishing) {
        const actionSkillId = selectActionSkillId(getState());

        await Promise.all([
          dispatch(
            listReleases({ reactivateSession: ReactivateSession.NEVER })
          ),
          dispatch(
            listEnvironments({ reactivateSession: ReactivateSession.NEVER })
          ),
          dispatch(
            listSnapshotsWithArgs({
              skillId: actionSkillId,
              reactivateSession: ReactivateSession.NEVER
            })
          )
        ]);
      }

      if (selectIsDialogEnabledInDraftEnvironment(getState())) {
        const dialogSkillId = selectDialogSkillId(getState());
        await dispatch(
          listSnapshotsWithArgs({
            skillId: dialogSkillId,
            reactivateSession: ReactivateSession.NEVER
          })
        );
      }

      return results;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);
